import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const milestones = [
  { year: '2014', title: '品牌创立', description: 'kaiyun com 正式创立，以正版授权、安全可靠为核心理念进入市场。' },
  { year: '2016', title: '用户突破百万', description: '凭借优质服务与口碑传播，平台注册用户突破100万大关。' },
  { year: '2018', title: '技术全面升级', description: '完成核心技术架构升级，引入分布式部署与智能风控系统。' },
  { year: '2020', title: '全球化布局', description: '服务范围扩展至全球150+个国家和地区，国际化战略全面落地。' },
  { year: '2023', title: '用户突破500万', description: '累计服务用户超过500万，平台稳定性与用户满意度持续领先行业。' },
  { year: '2026', title: '引领行业标准', description: '持续创新，以技术驱动行业进步，成为值得信赖的行业标杆。' },
];

const values = [
  { icon: 'ri-shield-star-line', title: '安全第一', desc: '始终将用户数据与资产安全放在首位，构建多层防护体系。' },
  { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户需求为导向，持续优化服务体验，用心做好每一个细节。' },
  { icon: 'ri-lightbulb-line', title: '创新驱动', desc: '持续投入技术研发，以创新驱动服务升级，引领行业发展方向。' },
  { icon: 'ri-hand-heart-line', title: '诚信为本', desc: '坚持合规经营、诚信服务，以真诚赢得每一位用户的信赖。' },
];

const trustBadges = [
  { icon: 'ri-verified-badge-line', title: '正版授权认证', desc: '持有官方正版授权资质' },
  { icon: 'ri-shield-check-line', title: '安全防护认证', desc: '通过国际安全标准审核' },
  { icon: 'ri-award-line', title: '行业优质品牌', desc: '连续多年获行业认可' },
  { icon: 'ri-star-smile-line', title: '用户满意品牌', desc: '用户满意度持续领先' },
];

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rfirsnlsngrm4lmbh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || t('contact.error'));
        }
        setFormStatus('error');
      }
    } catch {
      setFormError(t('contact.error'));
      setFormStatus('error');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: 'https://www.kaiyun.com' },
          { '@type': 'ListItem', position: 2, name: t('about.title') },
        ],
      },
      {
        '@type': 'AboutPage',
        name: t('about.title') + ' - ' + t('schema.org_name'),
        description: t('schema.description'),
        url: 'https://www.kaiyun.com/about',
      },
      {
        '@type': 'Organization',
        name: t('schema.org_name'),
        url: 'https://www.kaiyun.com',
        description: t('schema.description'),
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-XXX-XXXX',
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
          email: 'service@kaiyun.com',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb items={[{ label: t('about.title') }]} />

        {/* Hero */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              {t('about.title')}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
              {t('site.description')}
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">
              {t('about.brand_intro')}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10 max-w-2xl mx-auto">
              自创立以来，kaiyun com 始终秉持"用户至上、安全第一"的理念，致力于为用户打造值得信赖的一站式综合服务平台。
            </p>

            {/* Milestones */}
            <div className="relative">
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2" />
              <div className="space-y-8 lg:space-y-0">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`flex flex-col lg:flex-row items-center gap-4 lg:gap-8 ${
                      index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                        <span className="inline-block text-xs font-bold text-accent-700 bg-accent-100 px-2.5 py-0.5 rounded-full mb-2">
                          {item.year}
                        </span>
                        <h3 className="text-base font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    <div className="relative z-10 w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0">
                      <i className="ri-check-line" />
                    </div>
                    <div className="flex-1 hidden lg:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">
              {t('about.values.title')}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              四大核心价值观，指引我们不断前行
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {values.map((val, index) => (
                <div
                  key={index}
                  className="text-center p-6 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-100 text-accent-600">
                    <i className={`${val.icon} text-xl`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">
              {t('about.reputation')}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              多项权威认证，值得您的信赖
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {trustBadges.map((badge, index) => (
                <div
                  key={index}
                  className="group flex flex-col items-center text-center p-6 bg-background-50 border border-background-200/70 rounded-lg hover:border-accent-300 transition-all duration-300 cursor-default"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mb-4 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors duration-300">
                    <i className={`${badge.icon} text-2xl`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1.5">{badge.title}</h3>
                  <p className="text-sm text-foreground-500">{badge.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">
              {t('about.contact')}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              如有任何疑问，欢迎随时与我们联系
            </p>

            <div className="flex flex-col lg:flex-row gap-8 md:gap-10 max-w-5xl mx-auto">
              <div className="lg:w-80 flex-shrink-0 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">service@kaiyun.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h4>
                    <p className="text-sm text-foreground-500">400-XXX-XXXX</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-time-line text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">服务时间</h4>
                    <p className="text-sm text-foreground-500">7×24小时全天候在线</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-500">中国 · 深圳市南山区科技园</p>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <form
                  data-readdy-form=""
                  onSubmit={handleSubmit}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('contact.name')}
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        placeholder={t('contact.placeholder_name')}
                        className="w-full px-3.5 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('contact.email')}
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        placeholder={t('contact.placeholder_email')}
                        className="w-full px-3.5 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('contact.message')}
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder={t('contact.placeholder_message')}
                      className="w-full px-3.5 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                    />
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-honeypot-field"
                  />
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {t('contact.submit')}
                  </button>
                  {formStatus === 'success' && (
                    <p className="mt-3 text-sm text-green-600 font-medium">{t('contact.success')}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-3 text-sm text-red-600 font-medium">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}